import { defineComponent, h } from 'vue';
import { QCard, QCardSection, QCardActions, QBtn, QIcon, QTooltip, QSeparator } from 'quasar';
import BaseComponent from './baseFormMixin.jsx';
import arrayExpressionMixin from './arrayExpressionField.jsx';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import { Container, Draggable } from 'vue3-smooth-dnd';
import { ghostParentForIconOverlay } from '../vuecontrols/flowv2/canvas/iconOverlayDrag.js';

import TranslationContainer from '../TranslationContainer.jsx';

// DnD implemented via:
// https://kutlugsahin.github.io/vue-smooth-dnd

export default defineComponent({
    name: 'sform-array',
    mixins: [BaseComponent, arrayExpressionMixin],
    components: {
        QCard,
        QCardSection,
        QCardActions,
        QBtn,
        QIcon,
        QTooltip,
        QSeparator,
        Container,
        Draggable,
        TranslationContainer,
    },
    data: () => ({
        formitem: null,
        chosenitems: null,
        availableitems: null,
    }),
    props: {
        layouttype: null,
    },
    created() {
        if (this.form && this.form.length > 0) {
            this.formitem = this.form[0];
        }
    },
    mounted() {
        this.loadComplete();
    },
    computed: {
        addenabled: function () {
            if (this.availableitems && typeof this.availableitems === 'object')
                return Object.keys(this.availableitems).every(k => this.availableitems[k]);
            else
                return true;
        },
        addLabel: function () {
            return this.Translate(this.element.add || 'Add Another');
        },
        arrayChrome() {
            return utils.forms.getArrayChromeFlags({
                formElement: this.element,
                fieldSchema: this.element?.schema,
                appearanceReadonly: !!this.appearance?.readonly,
            });
        },
    },
    methods: {
        addNewItem(e) {
            if (!this.arrayChrome.allowAdd)
                return;

            if (!this.itemvalue)
                this.sync([]); // If the array itself is unassigned, initialize to an empty array

            if (this.itemvalue)
                this.itemvalue.push(utils.forms.mergeDefaultModel(null, this.element.schema.items, this));
        },
        removeItem(index) {
            if (!this.arrayChrome.allowRemove)
                return;
            this.itemvalue.splice(index, 1);
        },
        reOrderItems(result) {
            if (!this.arrayChrome.allowMove)
                return;
            // Ex: {"removedIndex":2,"addedIndex":0}
            const item = this.itemvalue[result.removedIndex];
            this.itemvalue.splice(result.removedIndex, 1);
            this.itemvalue.splice(result.addedIndex, 0, item);
        },
    },
    render() {
        const expression = this.renderArrayExpressionStandIn({ class: 'c-formArray', labelSlot: true });
        if (expression)
            return expression;

        if (!this.form || this.form.length < 1)
            return null;

        let element;
        let tag;
        let formtype;
        if (this.form.length > 1) {
            // A manually defined array can have multiple items, wrap in a fieldset
            // discard
            element = {
                type: 'fieldset',
                items: this.form,
            };
            tag = `sform-${element.type}`;
        }
        else {
            element = this.form[0];
            tag = this.layouttype;
            formtype = `sform-${element.type}`;
        }

        const items = [];
        const actions = [];

        let modelkeyroot;

        if (element.key) {
            modelkeyroot = [...element.key];

            if (this.modelkey && this.modelkey.length > 0)
                for (let j = 0; j < this.modelkey.length && j < modelkeyroot.length; j++)
                    modelkeyroot[j] = this.modelkey[j];
        }

        const Tag = utils.getComponentByTag(tag);

        if (this.itemvalue)
            for (let i = 0; i < this.itemvalue.length; i++) {
                // Take the index from the arra and replace the indexer expression []
                // within the key expression to include the literal index [{i}]
                let modelkey = modelkeyroot ? modelkeyroot.map(k => k == '[]' ? `[${i}]` : k) : [];
                let divide;
                if (i < this.itemvalue.length - 1)
                    divide = <QSeparator />;

                items.push(
                    <Draggable key={modelkey.join('/')} index={i} style="padding: 8px 0;">
                        <div class="draggable-item" style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Tag
                                style="flex-grow: 1; margin-right: 10px;"
                                name={this.getElementName(element)}
                                root={this.root}
                                element={element}
                                form={element.items}
                                cmodel={this.cmodel}
                                modelkey={modelkey}
                                depth={this.depth + 1}
                                array_index={i}
                                layouttype={this.layouttype}
                                formtype={formtype}
                                appearance={this.appearance}
                                nopadding={true}
                                type="FormField_ArrayItem"
                                isArrayItem={true}
                                notitle={true}
                                readonly={element.readonly || false}
                                schema={this.schema}
                                scopeitems={this.scopeitems}
                                controlscope={this.controlscope}
                            >
                            </Tag>
                            <span v-show={this.itemvalue.length > 1 && this.arrayChrome.allowMove} class="column-drag-handle" style={{ padding: '0 10px', marginTop: '13px', cursor: 'grab' }}>&#x2630;</span>
                            <QBtn
                                flat
                                round
                                dense
                                icon="mdi-close"
                                size="sm"
                                style="margin-top:12px;"
                                v-show={!this.noinput && this.arrayChrome.allowRemove}
                                onClick={(e) => this.removeItem(i)}
                            >

                                <QTooltip>
                                    Remove
                                </QTooltip>
                            </QBtn>
                        </div>
                        {divide}
                    </Draggable>
                );
            }

        if (!this.noinput && this.arrayChrome.allowAdd)
            actions.push(
                <QBtn
                    class="c-SentenceCaseButton"
                    icon="mdi-plus"
                    color="primary"
                    disable={!this.addenabled}
                    flat
                    no-caps
                    style="font-size: 14px; line-height: normal;"
                    onClick={(e) => this.addNewItem(e)}
                >
                        <span>{this.addLabel}</span>
                </QBtn>
            );

        let title;
        if (!this.element.notitle)
            title = (
                <QCardSection class="text-h6">
                    <TranslationContainer context={this} value={this.Title} />
                </QCardSection>
            );

        return (
            <QCard flat bordered class="c-formArray q-mb-sm card-array">
                {title}

                <QCardSection class="q-pt-none q-pb-none">
                    <Container v-show={this.itemvalue && this.itemvalue.length > 0} onDrop={(res) => this.reOrderItems(res)} drag-handle-selector=".column-drag-handle" getGhostParent={() => ghostParentForIconOverlay(this.$el)}>
                        {items}
                    </Container>
                </QCardSection>

                <QCardActions>
                    {actions}
                </QCardActions>
            </QCard>
        );
    }
});